/**
 * Centralized API endpoint paths — the single source of truth for every URL
 * the frontend hits. Mirrors the structure of `frontend/src/router/constant/
 * routes.ts` (which holds UI routes), keeping concerns separate but the
 * convention identical.
 *
 * - Static paths → string literals.
 * - Parameterized paths → arrow functions returning the full path.
 * - Dynamic segments are `encodeURIComponent`-wrapped where the existing
 *   call sites do so (preserving behaviour).
 */
export const API_ENDPOINTS = {
    AUTH: {
        LOGIN: '/auth/login',
        REGISTER: '/auth/register',
        REGISTER_VIA_INVITATION: '/auth/register-via-invitation',
        /** Sign an existing account in *and* accept its invitation in one call. */
        LOGIN_VIA_INVITATION: '/auth/login-via-invitation',
        LOGOUT: '/auth/logout',
        FORGOT_PASSWORD: '/auth/forgot-password',
        RESET_PASSWORD: '/auth/reset-password',
        VERIFY_RESET_TOKEN: '/auth/reset-password/verify',
        /**
         * OAuth sign-in. `OAUTH_START` is a full-page navigation target (the API
         * 302s to the provider); `OAUTH_EXCHANGE` swaps the callback's single-use
         * ticket for a session — same response shape as `LOGIN`.
         */
        OAUTH_START: (provider: string) => `/auth/oauth/${encodeURIComponent(provider)}/start`,
        OAUTH_EXCHANGE: '/auth/oauth/exchange',
    },
    USER: {
        ME: '/user',
        ALL: '/user/all',
        PASSWORD: '/user/password',
    },
    ROLE: {
        LIST: '/role',
        BY_ID: (id: string) => `/role/${id}`,
        MEMBERS: (id: string) => `/role/${id}/members`,
        ASSIGN: '/role/assign',
        MEMBER: '/role/member',
        MY_PERMISSIONS: '/role/permissions/me',
    },
    PERMISSION: {
        LIST: '/permission',
    },
    INVITATIONS: {
        LIST: '/invitations',
        BULK: '/invitations/bulk',
        VERIFY: '/invitations/verify',
        VERIFY_PUBLIC: '/invitations/public/verify',
        /** Decline with the emailed token only — usable without a session. */
        DECLINE_PUBLIC: '/invitations/public/decline',
        BY_ID: (id: string) => `/invitations/${id}`,
        RESEND: (id: string) => `/invitations/${id}/resend`,
        REVOKE: (id: string) => `/invitations/${id}/revoke`,
        ANSWER: (answer: 'accept' | 'decline') => `/invitations/${answer}`,
    },
    CONTACTS: {
        LIST: '/contacts',
        EXPORT: '/contacts/export',
        IDS: '/contacts/ids',
        BULK_DELETE: '/contacts/bulk-delete',
        BULK_EDIT: '/contacts/bulk-edit',
        BY_ID: (id: string) => `/contacts/${id}`,
        ACTIVITY: (id: string) => `/contacts/${id}/activity`,
        NOTES: (id: string) => `/contacts/${id}/notes`,
        NOTE_BY_ID: (id: string, noteId: string) => `/contacts/${id}/notes/${noteId}`,
        EMAILS: (id: string) => `/contacts/${id}/emails`,
        EMAIL_SUBSCRIPTION: (id: string) => `/contacts/${id}/email-subscription`,
        LINK_ANALYTICS: (id: string) => `/contacts/${id}/link-analytics`,
        FORMS: (id: string) => `/contacts/${id}/forms`,
        TAGS: (id: string) => `/contacts/${id}/tags`,
        TAG_BY_ID: (id: string, tagId: string) => `/contacts/${id}/tags/${tagId}`,
        PREFERRED_CALLER_ID: (id: string) => `/contacts/${id}/preferred-caller-id`,
        STAGE: (id: string) => `/contacts/${id}/stage`,
        MARK_WON: (id: string) => `/contacts/${id}/mark-won`,
        ESTIMATES: (id: string) => `/contacts/${id}/estimates`,
        ESTIMATE: (id: string, estimateId: string) => `/contacts/${id}/estimates/${estimateId}`,
        UNDO_DECISION: (id: string) => `/contacts/${id}/decision/undo`,
    },
    /** Unsent drafts for the Contact Details floating composer. Served by the
     *  inbox module (email drafts share `email_drafts` with the inbox
     *  composers), hence the `/inbox` prefix on a contacts-facing feature.
     *  `contactId` is a QUERY param, not a path param — see the controller. */
    CONTACT_DRAFTS: {
        LIST: '/inbox/contact-drafts',
        BY_ID: (id: string) => `/inbox/contact-drafts/${id}`,
        EMAIL_BY_ID: (id: string) => `/inbox/email/drafts/${id}`,
    },
    CONTACT_IMPORTS: {
        LIST: '/contact-imports',
        UPLOAD_URL: '/contact-imports/upload-url',
        BY_ID: (id: string) => `/contact-imports/${id}`,
    },
    CONVERSIONS: {
        /** `DELETE /conversions/:id` — voids a manually-recorded conversion (mark-won undo). */
        VOID: (id: string) => `/conversions/${id}`,
    },
    TAGS: {
        EXPORT: '/tags/export',
        LIST: '/tags',
        BY_ID: (id: string) => `/tags/${id}`,
    },
    SEARCH: {
        QUERY: '/search',
    },
    SEGMENTS: {
        LIST: '/segments',
        PREVIEW: '/segments/preview',
        BY_ID: (id: string) => `/segments/${id}`,
        PREVIEW_SEGMENT: (id: string) => `/segments/${id}/preview`,
        CONTACTS: (id: string) => `/segments/${id}/contacts`,
        CONTACTS_EXPORT: (id: string) => `/segments/${id}/contacts/export`,
        CLONE: (id: string) => `/segments/${id}/clone`,
        STATS: (id: string) => `/segments/${id}/stats`,
    },
    QUICK_MESSAGES: {
        LIST: '/quick-messages',
        BY_ID: (id: string) => `/quick-messages/${id}`,
    },
    JOIN_LINKS: {
        LIST: '/join-links',
        STATS: '/join-links/stats',
        BULK_DELETE: '/join-links/bulk-delete',
        SLUG_CHECK: '/join-links/slug/check',
        SLUG_SUGGEST: '/join-links/slug/suggest',
        BY_ID: (id: string) => `/join-links/${id}`,
        QR_STATS: (id: string) => `/join-links/${id}/qr-stats`,
        QR_DESIGN: (id: string) => `/join-links/${id}/qr-design`,
        ANALYTICS: (id: string) => `/join-links/${id}/analytics`,
        SUBMISSIONS_EXPORT: (id: string) => `/join-links/${id}/submissions/export`,
        SUBMISSIONS: (id: string) => `/join-links/${id}/submissions`,
    },
    JOIN_KEYWORDS: {
        LIST: '/join-keywords',
        AVAILABILITY: '/join-keywords/availability',
        DASHBOARD: '/join-keywords/dashboard',
        BY_ID: (id: string) => `/join-keywords/${id}`,
        PAUSE: (id: string) => `/join-keywords/${id}/pause`,
        RESUME: (id: string) => `/join-keywords/${id}/resume`,
        DUPLICATE: (id: string) => `/join-keywords/${id}/duplicate`,
        SUBSCRIBERS: (id: string) => `/join-keywords/${id}/subscribers`,
        SUBSCRIBERS_EXPORT: (id: string) => `/join-keywords/${id}/subscribers/export`,
        QR_DESIGN: (id: string) => `/join-keywords/${id}/qr-design`,
        ANALYTICS: (id: string) => `/join-keywords/${id}/analytics`,
        ACTIVITY: (id: string) => `/join-keywords/${id}/activity`,
        COMPARE: '/join-keywords/analytics/compare',
        INSIGHTS: '/join-keywords/insights',
    },
    SHORT_LINKS: {
        LIST: '/short-links',
        STATS: '/short-links/stats',
        BY_ID: (id: string) => `/short-links/${id}`,
        ANALYTICS: (id: string) => `/short-links/${id}/analytics`,
        QR: (id: string) => `/short-links/${id}/qr`,
        QR_URL: (id: string) => `/short-links/${id}/qr-url`,
        QR_DOWNLOAD: (id: string) => `/short-links/${id}/qr-download`,
        QR_STATS: (id: string) => `/short-links/${id}/qr-stats`,
        RESOLVE: (code: string, token?: string) =>
            token
                ? `/short-links/resolve/${encodeURIComponent(code)}/${encodeURIComponent(token)}`
                : `/short-links/resolve/${encodeURIComponent(code)}`,
    },
    PUBLIC_JOIN: {
        CONFIG: (slug: string) => `/join/${encodeURIComponent(slug)}`,
        OTP_SEND: (slug: string) => `/join/${encodeURIComponent(slug)}/otp/send`,
        SUBMIT: (slug: string) => `/join/${encodeURIComponent(slug)}/submit`,
        VIEW: (slug: string) => `/join/${encodeURIComponent(slug)}/view`,
    },
    LEAD_FORMS: {
        LIST: '/lead-forms',
        TEMPLATES: '/lead-forms/templates',
        THEMES: '/lead-forms/themes',
        THEME_BY_ID: (themeId: string) => `/lead-forms/themes/${themeId}`,
        BY_ID: (id: string) => `/lead-forms/${id}`,
        PUBLISH: (id: string) => `/lead-forms/${id}/publish`,
        ARCHIVE: (id: string) => `/lead-forms/${id}/archive`,
        DUPLICATE: (id: string) => `/lead-forms/${id}/duplicate`,
        QR_DESIGN: (id: string) => `/lead-forms/${id}/qr-design`,
        SUBMISSIONS: (id: string) => `/lead-forms/${id}/submissions`,
        SUBMISSIONS_EXPORT: (id: string) => `/lead-forms/${id}/submissions/export`,
        EXPORT: '/lead-forms/export',
        SUBMISSION_BY_ID: (id: string, submissionId: string) =>
            `/lead-forms/${id}/submissions/${submissionId}`,
        SUBMISSION_FILE_URL: (id: string, submissionId: string) =>
            `/lead-forms/${id}/submissions/${submissionId}/file`,
        ANALYTICS_ORG_OVERVIEW: '/lead-forms/analytics/overview',
        ANALYTICS_OVERVIEW: (id: string) => `/lead-forms/${id}/analytics/overview`,
        ANALYTICS_TIMESERIES: (id: string) => `/lead-forms/${id}/analytics/timeseries`,
        ANALYTICS_BREAKDOWNS: (id: string) => `/lead-forms/${id}/analytics/breakdowns`,
    },
    PUBLIC_FORMS: {
        CONFIG: (slug: string) => `/forms/${encodeURIComponent(slug)}`,
        VIEW: (slug: string) => `/forms/${encodeURIComponent(slug)}/view`,
        START: (slug: string) => `/forms/${encodeURIComponent(slug)}/start`,
        SUBMIT: (slug: string) => `/forms/${encodeURIComponent(slug)}/submit`,
        FILE_UPLOAD_URL: (slug: string) => `/forms/${encodeURIComponent(slug)}/file-upload-url`,
    },
    WELCOME_TEMPLATES: {
        LIST: '/welcome-templates',
        BY_ID: (id: string) => `/welcome-templates/${id}`,
    },
    ORGANIZATION: {
        CURRENT: '/organization',
        ALL: '/organization/all',
        SWITCH: '/organization/switch',
        CONTACT_CARD: '/organization/contact-card',
        BUSINESS_HOURS: '/organization/business-hours',
    },
    POLLS: {
        LIST: '/polls',
        BY_ID: (id: string) => `/polls/${id}`,
        CLOSE: (id: string) => `/polls/${id}/close`,
        CANCEL: (id: string) => `/polls/${id}/cancel`,
        ANALYTICS: (id: string) => `/polls/${id}/analytics`,
        CREATE_SEGMENT: (id: string) => `/polls/${id}/create-segment`,
        VOTES: (id: string) => `/polls/${id}/votes`,
        VOTES_EXPORT: (id: string) => `/polls/${id}/votes/export`,
        RECORD_VOTE: (id: string) => `/polls/${id}/votes/record`,
        MESSAGES: (id: string) => `/polls/${id}/messages`,
    },
    OPT_OUT: {
        KEYWORDS: '/opt-out/keywords',
        KEYWORD_BY_ID: (id: string) => `/opt-out/keywords/${id}`,
        REPLY_MESSAGE: '/opt-out/reply-message',
        PROVIDER_SYNC_STATUS: '/opt-out/provider-sync-status',
        PROVIDER_SYNC_STATUS_RESYNC: '/opt-out/provider-sync-status/resync',
    },
    OPT_IN: {
        KEYWORDS: '/opt-in/keywords',
        KEYWORD_BY_ID: (id: string) => `/opt-in/keywords/${id}`,
        REPLY_MESSAGE: '/opt-in/reply-message',
        PROVIDER_SYNC_STATUS: '/opt-in/provider-sync-status',
        PROVIDER_SYNC_STATUS_RESYNC: '/opt-in/provider-sync-status/resync',
    },
    DASHBOARD: {
        KPIS: '/dashboard/kpis',
        CAMPAIGN_PERFORMANCE: '/dashboard/campaign-performance',
        OPT_OUT_TRENDS: '/dashboard/opt-out-trends',
        CHANNEL_ENGAGEMENT: '/dashboard/channel-engagement',
        POLL_INSIGHTS: '/dashboard/poll-insights',
        LINK_ENGAGEMENT: '/dashboard/link-engagement',
        TODAY_SNAPSHOT: '/dashboard/today-snapshot',
        BUSINESS_KPIS: '/dashboard/business-kpis',
        REVENUE_SERIES: '/dashboard/revenue-series',
        AI_SUMMARY: '/dashboard/ai-summary',
        ACTIVITY: '/dashboard/activity',
    },
    DASHBOARDS: {
        LIST: '/dashboards',
        BY_ID: (id: string) => `/dashboards/${id}`,
        DUPLICATE: (id: string) => `/dashboards/${id}/duplicate`,
        SET_DEFAULT: (id: string) => `/dashboards/${id}/set-default`,
        SET_WORKSPACE_DEFAULT: (id: string) => `/dashboards/${id}/set-workspace-default`,
        SHARING: (id: string) => `/dashboards/${id}/sharing`,
    },
    CHANNELS_OVERVIEW: '/channels/overview',
    ONBOARDING: {
        STATUS: '/onboarding/status',
        WELCOME_SEEN: '/onboarding/welcome-seen',
        SKIP: '/onboarding/skip',
        DISMISS_RAIL: '/onboarding/dismiss-rail',
        CELEBRATE: '/onboarding/celebrate',
        WIZARD: '/onboarding/wizard',
        WIZARD_BUSINESS: '/onboarding/wizard/business',
        // Home-service lead conversion (Phase 2): one route per wizard step.
        // `wizard/goal` is gone — onboarding no longer asks for a goal.
        WIZARD_LEAD_PROFILE: '/onboarding/wizard/lead-profile',
        WIZARD_LEAD_SOURCE: '/onboarding/wizard/lead-source',
        WIZARD_PLAYBOOK: '/onboarding/wizard/playbook',
        WIZARD_AI_POLICY: '/onboarding/wizard/ai-policy',
        WIZARD_GO_LIVE: '/onboarding/wizard/go-live',
        TEST_LEAD: '/onboarding/test-lead',
        TEST_LEAD_TRACE: (contactId: string) => `/onboarding/test-lead/${contactId}`,
        WIZARD_ADVANCE: '/onboarding/wizard/advance',
        WIZARD_COMPLETE: '/onboarding/wizard/complete',
    },
    LEAD_RESPONSE: {
        PROFILE: '/lead-response/profile',
        TRADE_PACK_PREVIEW: '/lead-profile/trade-pack/preview',
        TRADE_PACK_APPLY: '/lead-profile/trade-pack/apply',
    },
    /**
     * Today — the daily lead-action workspace (`backend/src/modules/today`).
     * Every route is `GET` and gated on `dashboard:read`; the backend's global
     * pipe rejects unknown query params with a 400, so send only what the
     * DTOs whitelist (`group`/`period`/`assignedTo`/`page`/`limit`, `date`).
     */
    TODAY: {
        SUMMARY: '/today/summary',
        DIGEST: '/today/digest',
        DIGEST_DAYS: '/today/digest/days',
        SERIES: '/today/series',
        LEADS: '/today/leads',
        LEAD_SUMMARY: (contactId: string) => `/today/leads/${encodeURIComponent(contactId)}`,
        BOOKINGS: '/today/bookings',
        EMPLOYEES: '/today/employees',
    },
    /**
     * How the AI Lead Responder is doing. `from`/`to` are `YYYY-MM-DD` and go
     * together or not at all; absent, the server reports the last 30 days in
     * the ORGANIZATION's calendar. Same whitelisting rule as TODAY above.
     */
    AI_LEAD_RESPONDER: {
        METRICS: '/ai-lead-responder/metrics',
        HANDOFF_CONVERSATIONS: '/ai-lead-responder/handoffs/conversations',
    },
    /**
     * Outcome reporting. A window is a span (`days`) OR a range (`from`+`to`),
     * never both and never half a range; absent, the server reports the last
     * 30 days in the ORGANIZATION's calendar. `step` on the drill is required
     * — there is no unnamed "all leads" list. Same whitelisting rule as above.
     */
    OUTCOMES: {
        FUNNEL: '/outcomes/funnel',
        LEADS: '/outcomes/leads',
    },
    SERVICE_AREAS: {
        LIST: '/service-areas',
        BY_ID: (id: string) => `/service-areas/${id}`,
    },
    OUTCOME_SETS: {
        LIST: '/outcome-sets',
        BY_ID: (id: string) => `/outcome-sets/${id}`,
        OUTCOMES: (id: string) => `/outcome-sets/${id}/outcomes`,
    },
    MEETING_TYPES: {
        LIST: '/meeting-types',
        SLUG_AVAILABILITY: '/meeting-types/slug-availability',
        BY_ID: (id: string) => `/meeting-types/${id}`,
        ARCHIVE: (id: string) => `/meeting-types/${id}/archive`,
        ACTIVATE: (id: string) => `/meeting-types/${id}/activate`,
        DUPLICATE: (id: string) => `/meeting-types/${id}/duplicate`,
        FOLLOW_UPS: (id: string) => `/meeting-types/${id}/follow-ups`,
        FOLLOW_UP: (id: string, kind: string) => `/meeting-types/${id}/follow-ups/${kind}`,
    },
    ROUTING_FORMS: {
        LIST: '/routing-forms',
        SLUG_AVAILABILITY: '/routing-forms/slug-availability',
        BY_ID: (id: string) => `/routing-forms/${id}`,
        ARCHIVE: (id: string) => `/routing-forms/${id}/archive`,
        ACTIVATE: (id: string) => `/routing-forms/${id}/activate`,
        DUPLICATE: (id: string) => `/routing-forms/${id}/duplicate`,
    },
    AVAILABILITY: {
        SCHEDULES: '/availability/schedules',
        SCHEDULE_BY_ID: (id: string) => `/availability/schedules/${id}`,
        SCHEDULE_OVERRIDES: (id: string) => `/availability/schedules/${id}/overrides`,
    },
    BOOKINGS: {
        LIST: '/bookings',
        EXPORT: '/bookings/export',
        BY_ID: (id: string) => `/bookings/${id}`,
        CANCEL: (id: string) => `/bookings/${id}/cancel`,
        COMPLETE: (id: string) => `/bookings/${id}/complete`,
        NO_SHOW: (id: string) => `/bookings/${id}/no-show`,
        OUTCOME: (id: string) => `/bookings/${id}/outcome`,
        RESCHEDULE: (id: string) => `/bookings/${id}/reschedule`,
        /** Authenticated slot options for moving this booking (excludes itself). */
        RESCHEDULE_SLOTS: (id: string) => `/bookings/${id}/reschedule-slots`,
        /** Suggested times for "Add appointment" (staff, not the public page). */
        STAFF_SLOTS: '/bookings/slots',
    },
    CALENDAR: {
        EVENTS: '/calendar/events',
        /** Company days off. */
        BLOCKS: '/calendar/blocks',
        BLOCK_BY_ID: (id: string) => `/calendar/blocks/${id}`,
    },
    CALENDAR_INTEGRATIONS: {
        PROVIDERS: '/calendar-integrations/providers',
        AUTHORIZE: (provider: string) =>
            `/calendar-integrations/${encodeURIComponent(provider)}/authorize`,
        CONNECTIONS: '/calendar-integrations/connections',
        CONNECTION_BY_ID: (id: string) => `/calendar-integrations/connections/${id}`,
        REFRESH_CALENDARS: (id: string) =>
            `/calendar-integrations/connections/${id}/refresh-calendars`,
        CONNECTION_CALENDARS: (id: string) => `/calendar-integrations/connections/${id}/calendars`,
    },
    /**
     * The field-service systems a business already runs on — Jobber, JobNimbus.
     *
     * `WEBHOOK_URL` is its own call and not a field on the connection list
     * because the URL contains that connection's secret: it is fetched once,
     * by somebody setting the automation up, never alongside a list that
     * read-only screens and error reports can pick up.
     */
    FIELD_SERVICE: {
        PROVIDERS: '/field-service/providers',
        CONNECTIONS: '/field-service/connections',
        JOBBER_AUTHORIZE: '/field-service/jobber/authorize',
        JOBNIMBUS_CONNECT: '/field-service/jobnimbus/connect',
        CONNECTION_BY_ID: (id: string) => `/field-service/connections/${id}`,
        STATUS_MAP: (id: string) => `/field-service/connections/${id}/status-map`,
        STATUS_MAP_OBSERVED: (id: string) => `/field-service/connections/${id}/status-map/observed`,
        WEBHOOK_URL: (id: string) => `/field-service/connections/${id}/webhook-url`,
        SYNC_NOW: (id: string) => `/field-service/connections/${id}/sync-now`,
        LINKS: '/field-service/links',
        NEEDS_A_PERSON: '/field-service/links/needs-a-person',
        MATCH_LINK: (id: string) => `/field-service/links/${id}/match`,
        SEND_LEAD: (contactId: string) => `/field-service/leads/${contactId}/send`,
    },
    PUBLIC_ROUTING_FORM: {
        GET: (orgSlug: string, formSlug: string) =>
            `/book/${encodeURIComponent(orgSlug)}/r/${encodeURIComponent(formSlug)}`,
    },
    PUBLIC_BOOKING: {
        CONFIG: (orgSlug: string, meetingSlug: string) =>
            `/book/${encodeURIComponent(orgSlug)}/${encodeURIComponent(meetingSlug)}`,
        SLOTS: (orgSlug: string, meetingSlug: string) =>
            `/book/${encodeURIComponent(orgSlug)}/${encodeURIComponent(meetingSlug)}/slots`,
        SUBMIT: (orgSlug: string, meetingSlug: string) =>
            `/book/${encodeURIComponent(orgSlug)}/${encodeURIComponent(meetingSlug)}`,
        /** Is this ZIP inside the type's accepted areas (`?postalCode=`). */
        SERVICE_AREA: (orgSlug: string, meetingSlug: string) =>
            `/book/${encodeURIComponent(orgSlug)}/${encodeURIComponent(meetingSlug)}/service-area`,
        /** A visitor outside the service area leaving their details for the team. */
        OUTSIDE_AREA: (orgSlug: string, meetingSlug: string) =>
            `/book/${encodeURIComponent(orgSlug)}/${encodeURIComponent(meetingSlug)}/outside-area`,
        MANAGE: (token: string) => `/book/manage/${encodeURIComponent(token)}`,
        /** Raw text/calendar download — linked to directly, not fetched via axios. */
        MANAGE_ICS: (token: string) => `/book/manage/${encodeURIComponent(token)}/calendar.ics`,
        /** Slot options for this booking's own reschedule (excludes itself). */
        MANAGE_SLOTS: (token: string) => `/book/manage/${encodeURIComponent(token)}/slots`,
        MANAGE_CANCEL: (token: string) => `/book/manage/${encodeURIComponent(token)}/cancel`,
        MANAGE_RESCHEDULE: (token: string) =>
            `/book/manage/${encodeURIComponent(token)}/reschedule`,
    },
    SMS_CHANNEL: {
        CONNECT: '/channels/sms/connect',
        DISCONNECT: '/channels/sms/disconnect',
        STATE: '/channels/sms/state',
        SELECT_RESOURCES: '/channels/sms/select-resources',
        DISCOVERY_REFRESH: '/channels/sms/discovery/refresh',
        BRAND: '/channels/sms/brand',
        BRAND_BY_SID: (sid: string) => `/channels/sms/brand/${encodeURIComponent(sid)}`,
        CAMPAIGN: '/channels/sms/campaign',
        CAMPAIGN_BY_SID: (sid: string) => `/channels/sms/campaign/${encodeURIComponent(sid)}`,
        MESSAGING_SERVICE: '/channels/sms/messaging-service',
        MESSAGING_SERVICE_ASSIGNMENT: '/channels/sms/messaging-service/assignment',
        PHONE_NUMBERS_SEARCH: '/channels/sms/phone-numbers/search',
        PHONE_NUMBERS_PURCHASE: '/channels/sms/phone-numbers/purchase',
        PROVIDER_PHONE_NUMBER_ASSIGN: (sid: string) =>
            `/channels/sms/provider/phone-numbers/${encodeURIComponent(sid)}/assign`,
    },
    WHATSAPP_CHANNEL: {
        CONNECT: '/channels/whatsapp/connect',
        DISCONNECT: '/channels/whatsapp/disconnect',
        STATE: '/channels/whatsapp/state',
    },
    WHATSAPP_TEMPLATES: {
        BASE: '/channels/whatsapp/templates',
        BY_ID: (id: string) => `/channels/whatsapp/templates/${encodeURIComponent(id)}`,
        CLONE: (id: string) => `/channels/whatsapp/templates/${encodeURIComponent(id)}/clone`,
        SUBMIT: (id: string) => `/channels/whatsapp/templates/${encodeURIComponent(id)}/submit`,
        SYNC: '/channels/whatsapp/templates/sync',
        SEND_SETTINGS: (id: string) =>
            `/channels/whatsapp/templates/${encodeURIComponent(id)}/send-settings`,
        FAVORITE: (id: string) => `/channels/whatsapp/templates/${encodeURIComponent(id)}/favorite`,
    },
    MODULE_BUILDER: {
        MODULES: '/modules',
        MODULES_REORDER: '/modules/reorder',
        MODULE_BY_ID: (moduleId: string) => `/modules/${moduleId}`,
        SECTIONS: (moduleId: string) => `/modules/${moduleId}/sections`,
        SECTIONS_REORDER: (moduleId: string) => `/modules/${moduleId}/sections/reorder`,
        SECTION_BY_ID: (moduleId: string, sectionId: string) =>
            `/modules/${moduleId}/sections/${sectionId}`,
        FIELDS: (moduleId: string) => `/modules/${moduleId}/fields`,
        FIELDS_REORDER: (moduleId: string) => `/modules/${moduleId}/fields/reorder`,
        FIELD_BY_ID: (moduleId: string, fieldId: string) =>
            `/modules/${moduleId}/fields/${fieldId}`,
        FIELD_MOVE: (moduleId: string, fieldId: string) =>
            `/modules/${moduleId}/fields/${fieldId}/move`,
        RECORDS: (moduleId: string) => `/modules/${moduleId}/records`,
        RECORD_BY_ID: (moduleId: string, recordId: string) =>
            `/modules/${moduleId}/records/${recordId}`,
        LAYOUTS: (moduleId: string) => `/modules/${moduleId}/layouts`,
        LAYOUT_BY_ID: (moduleId: string, layoutId: string) =>
            `/modules/${moduleId}/layouts/${layoutId}`,
        CONTACT_CUSTOM_FIELDS: (contactId: string) => `/contacts/${contactId}/custom-fields`,
    },
    WORKFLOWS: {
        LIST: '/workflows',
        CATALOG: '/workflows/catalog',
        BY_CONTACT: (contactId: string) => `/workflows/by-contact/${contactId}`,
        BY_ID: (id: string) => `/workflows/${id}`,
        VALIDATE: (id: string) => `/workflows/${id}/validate`,
        PUBLISH: (id: string) => `/workflows/${id}/publish`,
        UNPUBLISH: (id: string) => `/workflows/${id}/unpublish`,
        ARCHIVE: (id: string) => `/workflows/${id}/archive`,
        UNARCHIVE: (id: string) => `/workflows/${id}/unarchive`,
        PAUSE: (id: string) => `/workflows/${id}/pause`,
        RESUME: (id: string) => `/workflows/${id}/resume`,
        AUDIT: (id: string) => `/workflows/${id}/audit`,
        CLONE: (id: string) => `/workflows/${id}/clone`,
        EXECUTE: (id: string) => `/workflows/${id}/execute`,
        RUNS: (id: string) => `/workflows/${id}/runs`,
        RUNS_EXPORT: (id: string) => `/workflows/${id}/runs/export`,
        TEST_RUNS: (id: string) => `/workflows/${id}/test-runs`,
        READINESS: (id: string) => `/workflows/${id}/readiness`,
        WEBHOOK_ENDPOINTS: (id: string) => `/workflows/${id}/webhook-endpoints`,
        ANALYTICS: (id: string) => `/workflows/${id}/analytics`,
        BACKFILL: (id: string) => `/workflows/${id}/backfill`,
        VERSIONS: (id: string) => `/workflows/${id}/versions`,
        VERSION_RESTORE: (id: string, versionId: string) =>
            `/workflows/${id}/versions/${versionId}/restore`,
        NODE_TEST_SEND: (id: string, nodeId: string) =>
            `/workflows/${id}/nodes/${nodeId}/test-send`,
        ATTACHMENT_PRESIGN: '/workflows/attachments/presign',
    },
    WORKFLOW_TEMPLATES: {
        LIST: '/workflow-templates',
        USE: (templateId: string) => `/workflow-templates/${templateId}/use`,
        BY_ID: (id: string) => `/workflow-templates/${id}`,
    },
    SEQUENCES: {
        LIST: '/sequences',
        BY_ID: (id: string) => `/sequences/${id}`,
        VALIDATE: (id: string) => `/sequences/${id}/validate`,
        PUBLISH: (id: string) => `/sequences/${id}/publish`,
        PAUSE: (id: string) => `/sequences/${id}/pause`,
        RESUME: (id: string) => `/sequences/${id}/resume`,
        ARCHIVE: (id: string) => `/sequences/${id}/archive`,
        UNARCHIVE: (id: string) => `/sequences/${id}/unarchive`,
        CLONE: (id: string) => `/sequences/${id}/clone`,
        ANALYTICS: (id: string) => `/sequences/${id}/analytics`,
        CONTACTS: (id: string) => `/sequences/${id}/analytics/contacts`,
        REMOVE_CONTACT: (id: string, contactId: string) => `/sequences/${id}/contacts/${contactId}`,
        STEP_TEST_SEND: (id: string, stepId: string) =>
            `/sequences/${id}/steps/${stepId}/test-send`,
        AUDIENCE_ESTIMATE: '/sequences/audience/estimate',
        BULK_ENROLL: '/sequences/bulk-enroll',
        BULK_UNENROLL: '/sequences/bulk-unenroll',
        BY_CONTACT: (contactId: string) => `/sequences/by-contact/${contactId}`,
    },
    SEQUENCE_TEMPLATES: {
        LIST: '/sequence-templates',
        BY_KEY: (key: string) => `/sequence-templates/${key}`,
    },
    WORKFLOW_RUNS: {
        BY_ID: (runId: string) => `/workflow-runs/${runId}`,
        STEPS: (runId: string) => `/workflow-runs/${runId}/steps`,
        CANCEL: (runId: string) => `/workflow-runs/${runId}/cancel`,
        TEST_STATE: (runId: string) => `/workflow-runs/${runId}/test-state`,
        SKIP_WAIT: (runId: string) => `/workflow-runs/${runId}/skip-wait`,
        SIMULATE_EVENT: (runId: string) => `/workflow-runs/${runId}/simulate-event`,
    },
    ADVERTISING: {
        AUTHORIZE_URL: (provider: string) =>
            `/advertising/connections/${encodeURIComponent(provider)}/authorize-url`,
        PROVIDERS: '/advertising/providers',
        CONNECTIONS: '/advertising/connections',
        CONNECTION_LEAD_WEBHOOK: (id: string) => `/advertising/connections/${id}/lead-webhook`,
        CONNECTION_BY_ID: (id: string) => `/advertising/connections/${id}`,
        CONNECTION_ACCOUNTS: (id: string) => `/advertising/connections/${id}/accounts`,
        ACCOUNT_BY_ID: (id: string) => `/advertising/accounts/${id}`,
        CAMPAIGNS: '/advertising/campaigns',
        LEAD_FORMS: '/advertising/lead-forms',
        LEADS: '/advertising/leads',
        LEAD_BY_ID: (id: string) => `/advertising/leads/${id}`,
        ANALYTICS_CAMPAIGNS: '/advertising/analytics/campaigns',
        ANALYTICS_LEAD_SOURCES: '/advertising/analytics/lead-sources',
        ANALYTICS_FUNNEL: '/advertising/analytics/funnel',
        DEV_SIMULATE_LEAD: '/advertising/dev/simulate-lead',
    },
    ANALYTICS: {
        KPIS: '/analytics/overview/kpis',
        FUNNEL: '/analytics/overview/funnel',
        CHANNELS: '/analytics/overview/channels',
        CAMPAIGNS: '/analytics/overview/campaigns',
        CAMPAIGNS_EXPORT: '/analytics/overview/campaigns/export',
        MARKETING: {
            KPIS: '/analytics/marketing/kpis',
            SERIES: '/analytics/marketing/series',
            SOURCES: '/analytics/marketing/sources',
        },
        CONVERSATIONS: {
            KPIS: '/analytics/conversations/kpis',
            SERIES: '/analytics/conversations/series',
            SPLIT: '/analytics/conversations/split',
            HOURS: '/analytics/conversations/hours',
        },
        INSIGHTS: {
            HEALTH: '/analytics/insights/health',
            COACH: '/analytics/insights/coach',
        },
        REVENUE_KPIS: '/analytics/revenue/kpis',
        REVENUE_CAMPAIGNS: '/analytics/revenue/campaigns',
        REVENUE_SOURCES: '/analytics/revenue/sources',
        APPOINTMENTS: {
            KPIS: '/analytics/appointments/kpis',
            SERIES: '/analytics/appointments/series',
            MEETING_TYPES: '/analytics/appointments/meeting-types',
            SOURCES: '/analytics/appointments/sources',
        },
        DELIVERABILITY: {
            KPIS: '/analytics/deliverability/kpis',
            SMS_FAILURES: '/analytics/deliverability/sms-failures',
            EMAIL_FUNNEL: '/analytics/deliverability/email-funnel',
            OPT_OUTS: '/analytics/deliverability/opt-outs',
            COMPLIANCE_STATUS: '/analytics/deliverability/compliance-status',
        },
    },
    BUG_REPORTS: {
        LIST: '/bug-reports',
        BY_ID: (id: string) => `/bug-reports/${id}`,
        COMMENTS: (id: string) => `/bug-reports/${id}/comments`,
        ATTACHMENT_UPLOAD_URL: '/bug-reports/attachments/upload-url',
    },
    TICKETS: {
        LIST: '/tickets',
        BOARD: '/tickets/board',
        EXPORT: '/tickets/export',
        BULK: '/tickets/bulk',
        BY_ID: (id: string) => `/tickets/${id}`,
        STATUS: (id: string) => `/tickets/${id}/status`,
        ACTIVITY: (id: string) => `/tickets/${id}/activity`,
        TAGS: (id: string) => `/tickets/${id}/tags`,
        TAG_BY_ID: (id: string, tagId: string) => `/tickets/${id}/tags/${tagId}`,
        COMMENTS: (id: string) => `/tickets/${id}/comments`,
        COMMENT_BY_ID: (id: string, commentId: string) => `/tickets/${id}/comments/${commentId}`,
        ATTACHMENTS: (id: string) => `/tickets/${id}/attachments`,
        ATTACHMENT_UPLOAD_URL: (id: string) => `/tickets/${id}/attachments/upload-url`,
        ATTACHMENT_BY_ID: (id: string, attachmentId: string) =>
            `/tickets/${id}/attachments/${attachmentId}`,
        ATTACHMENT_DOWNLOAD_URL: (id: string, attachmentId: string) =>
            `/tickets/${id}/attachments/${attachmentId}/download-url`,
        CUSTOM_FIELDS: (id: string) => `/tickets/${id}/custom-fields`,
    },
    OPPORTUNITIES: {
        LIST: '/opportunities',
        BOARD: '/opportunities/board',
        EXPORT: '/opportunities/export',
        SUMMARY: '/opportunities/summary',
        REPORTS: '/opportunities/reports',
        ANALYTICS: '/opportunities/analytics',
        BULK: '/opportunities/bulk',
        BULK_DELETE: '/opportunities/bulk-delete',
        SOURCE_SUGGESTIONS: '/opportunities/source-suggestions',
        BY_ID: (id: string) => `/opportunities/${id}`,
        STAGE: (id: string) => `/opportunities/${id}/stage`,
        ACTIVITY: (id: string) => `/opportunities/${id}/activity`,
        TAGS: (id: string) => `/opportunities/${id}/tags`,
        TAG_BY_ID: (id: string, tagId: string) => `/opportunities/${id}/tags/${tagId}`,
        COMMENTS: (id: string) => `/opportunities/${id}/comments`,
        MENTION_CANDIDATES: '/opportunities/mention-candidates',
        COMMENT_BY_ID: (id: string, commentId: string) =>
            `/opportunities/${id}/comments/${commentId}`,
        ATTACHMENTS: (id: string) => `/opportunities/${id}/attachments`,
        ATTACHMENT_UPLOAD_URL: (id: string) => `/opportunities/${id}/attachments/upload-url`,
        ATTACHMENT_BY_ID: (id: string, attachmentId: string) =>
            `/opportunities/${id}/attachments/${attachmentId}`,
        ATTACHMENT_DOWNLOAD_URL: (id: string, attachmentId: string) =>
            `/opportunities/${id}/attachments/${attachmentId}/download-url`,
        CUSTOM_FIELDS: (id: string) => `/opportunities/${id}/custom-fields`,
        TASKS: (id: string) => `/opportunities/${id}/tasks`,
        TASK_BY_ID: (id: string, taskId: string) => `/opportunities/${id}/tasks/${taskId}`,
        TASK_COMPLETE: (id: string, taskId: string) =>
            `/opportunities/${id}/tasks/${taskId}/complete`,
        TASK_REOPEN: (id: string, taskId: string) => `/opportunities/${id}/tasks/${taskId}/reopen`,
    },
    PIPELINES: {
        LIST: '/pipelines',
        BY_ID: (id: string) => `/pipelines/${id}`,
        STAGES: (id: string) => `/pipelines/${id}/stages`,
        STAGE_BY_ID: (id: string, stageId: string) => `/pipelines/${id}/stages/${stageId}`,
        STAGES_REORDER: (id: string) => `/pipelines/${id}/stages/reorder`,
        LOST_REASONS: '/pipelines/lost-reasons',
        LOST_REASON_BY_ID: (id: string) => `/pipelines/lost-reasons/${id}`,
    },
    OPPORTUNITY_FIELDS: {
        LIST: '/opportunity-fields',
        BY_ID: (id: string) => `/opportunity-fields/${id}`,
    },
    VOICE_AGENT: {
        VOICES: '/voice-agent/voices',
        VOICES_LIBRARY: '/voice-agent/voices/library',
        VOICES_ADOPT: '/voice-agent/voices/adopt',
        CALLS: '/voice-agent/calls',
        CALL_BY_ID: (id: string) => `/voice-agent/calls/${id}`,
        CALL_RECORDING_URL: (id: string) => `/voice-agent/calls/${id}/recording`,
        /** P9-10 — regenerate the summary + analysis from the saved transcript. */
        CALL_ANALYSIS: (id: string) => `/voice-agent/calls/${id}/analysis`,
        /** P10-5 — every employee's phone at a glance; P10-4 — the workspace minute alert. */
        PHONES: '/voice-agent/phones',
        SETTINGS: '/voice-agent/settings',
    },
    VOICE: {
        SETTINGS: '/voice/settings',
        TOKEN: '/voice/token',
        PRESENCE: '/voice/presence',
        PRESENCE_ME: '/voice/presence/me',
        NUMBERS: '/voice/numbers',
        NUMBERS_MINE: '/voice/numbers/mine',
        NUMBER_CONFIG: (channelIdentityId: string) => `/voice/numbers/${channelIdentityId}/config`,
        CALLS: '/voice/calls',
        CALLS_EXPORT: '/voice/calls/export',
        CALL_ANALYTICS: '/voice/calls/analytics',
        CALL_BY_ID: (id: string) => `/voice/calls/${id}`,
        /** An AI-answered call on the Calls page (voice-calls:read). */
        CALL_AI_BY_ID: (id: string) => `/voice/calls/ai/${id}`,
        CALL_AI_RECORDING_URL: (id: string) => `/voice/calls/ai/${id}/recording`,
        /** P9-10 — regenerate on the Calls surface, and link a call to a contact. */
        CALL_AI_ANALYSIS: (id: string) => `/voice/calls/ai/${id}/analysis`,
        CALL_AI_CONTACT: (id: string) => `/voice/calls/ai/${id}/contact`,
        CALL_END: (id: string) => `/voice/calls/${id}/end`,
        CALL_HOLD: (id: string) => `/voice/calls/${id}/hold`,
        CALL_RESUME: (id: string) => `/voice/calls/${id}/resume`,
        CALL_TRANSFER: (id: string) => `/voice/calls/${id}/transfer`,
        CALL_PICK: (id: string) => `/voice/calls/${id}/pick`,
        CALL_DISPOSITION: (id: string) => `/voice/calls/${id}/disposition`,
        CALL_CONTACT: (id: string) => `/voice/calls/${id}/contact`,
        GREETING_UPLOAD_URL: '/voice/greetings/upload-url',
        GREETING_PLAYBACK_URL: '/voice/greetings/playback-url',
        BLOCKED_NUMBERS: '/voice/blocked-numbers',
        BLOCKED_NUMBER_BY_ID: (id: string) => `/voice/blocked-numbers/${id}`,
        RECORDINGS: '/voice/recordings',
        RECORDING_URL: (id: string) => `/voice/recordings/${id}/url`,
        RECORDING_HEARD: (id: string) => `/voice/recordings/${id}/heard`,
        RECORDING_BY_ID: (id: string) => `/voice/recordings/${id}`,
    },
} as const;
